import * as React from 'react';
import { useSetRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import { Picker, type PickerItemData } from '@gitbook/react-uikit';
import {
    Description,
    FieldGroup,
    Group,
    Heading,
    Label,
    Panel,
    PanelBody,
    Switch,
} from '@gitbook/spine-react';

import { sitePreviewAtom } from '../../SitePreview';
import type { SitesCustomizationPanelProps } from '../types';

const THEME_MODES_PICKER: PickerItemData<api.CustomizationThemeMode>[] = [
    {
        value: api.CustomizationThemeMode.Light,
        label: 'Light',
    },
    {
        value: api.CustomizationThemeMode.Dark,
        label: 'Dark',
    },
];

/**
 * Handle the theme mode settings, enable dark/light mode in the published content.
 */
export function ModesPanel(props: SitesCustomizationPanelProps): React.ReactElement {
    const { settings, setSettings } = props;

    const setPreviewOptions = useSetRecoilState(sitePreviewAtom);

    React.useEffect(() => {
        setPreviewOptions((prev) => ({
            ...prev,
            theme: settings.themes.default,
        }));
    }, [settings.themes.default, setPreviewOptions]);

    const additionalLabelDescription = settings.themes.toggeable
        ? 'Your viewers system setting is respected by default.'
        : '';

    return (
        <Panel>
            <Heading>Modes</Heading>
            <PanelBody>
                <FieldGroup orientation="horizontal">
                    <Group>
                        <Label>Show mode toggle</Label>
                        <Description>
                            Allow users to toggle the light/dark/system mode.{' '}
                            {additionalLabelDescription}
                        </Description>
                    </Group>
                    <Switch
                        value={settings.themes.toggeable}
                        onValueChange={(checked) => {
                            setSettings((prev) => ({
                                ...prev,
                                themes: {
                                    ...prev.themes,
                                    toggeable: checked,
                                },
                            }));
                        }}
                    />
                </FieldGroup>

                {settings.themes.toggeable ? null : (
                    <FieldGroup orientation="horizontal">
                        <Group>
                            <Label>Default mode</Label>
                            <Description>
                                All your viewers will see your site in{' '}
                                {settings.themes.default.toLowerCase()} mode.
                            </Description>
                        </Group>
                        <Picker
                            value={settings.themes.default}
                            items={THEME_MODES_PICKER}
                            onValueChange={(newMode) =>
                                setSettings((prev) => ({
                                    ...prev,
                                    themes: {
                                        ...prev.themes,
                                        default: newMode,
                                    },
                                }))
                            }
                        />
                    </FieldGroup>
                )}
            </PanelBody>
        </Panel>
    );
}
